// FlavorFamilyTagSelector — colored chip cloud, multi-select per inventory
// item. The user can add a new family with a color picker. Ported from
// tag-selectors.jsx (FamilyTagSelector at lines 47-181), modified for
// multi-select (Decision Point 1 — matches platform.inventory_tag_flavor_family
// being M:N via inventory_tag_flavor_family_rel).

import { useState } from "react";
import { SEED_COLORS } from "./constants";
import type { TagFlavorFamily } from "./_types";

const Plus = ({ size = 10 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

type FamilyChipProps = {
  family: TagFlavorFamily;
  onClick?: () => void;
  selected?: boolean;
};

function FamilyChip({ family, onClick, selected }: FamilyChipProps) {
  const c = family.color;
  return (
    <span
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px 4px 7px",
        borderRadius: 999,
        background: selected ? c : "var(--panel)",
        color: selected ? "#fff" : "var(--ink-2)",
        border: `1px solid ${selected ? c : "var(--line-strong)"}`,
        cursor: onClick ? "pointer" : "default",
        fontSize: 11.5,
        fontWeight: 500,
        boxShadow: selected ? "0 1px 4px -1px rgba(20,30,22,0.18)" : "none",
        transition: "all 140ms ease",
      }}
    >
      <span
        style={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          background: c,
          boxShadow: selected
            ? "inset 0 0 0 1.5px rgba(255,255,255,0.5)"
            : "none",
        }}
      />
      {family.label}
    </span>
  );
}

type Props = {
  /** Currently-selected flavor family ids. Multi-select. */
  value: string[];
  onToggle: (familyId: string) => void;
  families: TagFlavorFamily[];
  onAddFamily?: (f: Omit<TagFlavorFamily, "id"> & { id?: string }) => void;
};

export function FlavorFamilyTagSelector({
  value,
  onToggle,
  families,
  onAddFamily,
}: Props) {
  const [adding, setAdding] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftColor, setDraftColor] = useState(SEED_COLORS[0]!);

  const commit = () => {
    if (!draftName.trim()) return;
    onAddFamily?.({
      label: draftName.trim(),
      color: draftColor,
      isDefault: false,
    });
    setAdding(false);
    setDraftName("");
  };

  return (
    <div className="vstack" style={{ gap: 8 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {families.map((f) => (
          <FamilyChip
            key={f.id}
            family={f}
            selected={value.includes(f.id)}
            onClick={() => onToggle(f.id)}
          />
        ))}
        {!adding && onAddFamily && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "4px 10px",
              borderRadius: 999,
              background: "transparent",
              border: "1px dashed var(--line-strong)",
              color: "var(--ink-3)",
              fontSize: 11.5,
              cursor: "pointer",
            }}
          >
            <Plus size={10} />
            New family
          </button>
        )}
      </div>

      {adding && (
        <div
          style={{
            padding: 12,
            borderRadius: 8,
            background: "var(--bg-sunk)",
            border: "1px solid var(--line)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: draftColor,
                border: "1px solid rgba(0,0,0,0.08)",
                flexShrink: 0,
              }}
            />
            <input
              autoFocus
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") setAdding(false);
              }}
              placeholder="Family name — e.g. Marine, Umami, Roasted nut…"
              style={{
                flex: 1,
                padding: "6px 9px",
                fontSize: 12,
                border: "1px solid var(--line)",
                borderRadius: 4,
                background: "var(--panel)",
                outline: "none",
              }}
            />
          </div>
          <div
            className="hstack"
            style={{
              gap: 5,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span
              className="mono"
              style={{
                fontSize: 9.5,
                color: "var(--ink-4)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginRight: 4,
              }}
            >
              Color
            </span>
            {SEED_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setDraftColor(c)}
                aria-label={`Color ${c}`}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: c,
                  border:
                    c === draftColor
                      ? "2px solid var(--ink)"
                      : "1px solid rgba(0,0,0,0.12)",
                  outline: c === draftColor ? "1px solid var(--bg)" : "none",
                  outlineOffset: c === draftColor ? -3 : 0,
                  cursor: "pointer",
                  padding: 0,
                }}
              />
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 6,
            }}
          >
            <button className="btn xs" onClick={() => setAdding(false)}>
              Cancel
            </button>
            <button
              className="btn primary xs"
              disabled={!draftName.trim()}
              style={{ opacity: draftName.trim() ? 1 : 0.4 }}
              onClick={commit}
            >
              Add &amp; select
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
